import * as Sentry from '@sentry/browser';
import { FieldName } from 'netvalve-types';
import {
    NetvalveCardNumber,
    NetvalveCVV,
    NetvalveExpiry,
    NetvalveTokenizerBase,
    NetvalveTokenFieldsContainer,
} from './components';
import { applyCommonAttributes, getFormElement, getQueryParams, sentryLog, wrapCallback } from './helpers';
import { TokenFieldsConfiguration } from './types';
import { SENTRY_APPLICATION_KEY } from './constants';

declare global {
    interface Window {
        // Merchant interface - sdk methods
        Netvalve: {
            initTokenFields: (config: TokenFieldsConfiguration) => void;
            tokenizeFields?: () => Promise<string | null>;
            allFieldsTokenized?: () => boolean;
            validateFields?: () => Promise<boolean>;
            isTokenizing?: () => boolean;
        };
    }
}
// Sentry tracking enabled in dev and uat
if (import.meta.env.VITE_APP_ENV === 'uat' || import.meta.env.VITE_APP_ENV === 'production') {
    Sentry.init({
        environment: `${import.meta.env.VITE_APP_ENV}`,
        dsn: `${import.meta.env?.VITE_APP_SENTRY_DSN}`,
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: [SENTRY_APPLICATION_KEY],
                behaviour: 'drop-error-if-contains-third-party-frames',
            }),
        ],
        tracesSampleRate: 0.7, //  Capture 70% of the transactions. Will reduce to 10-30
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 0.5,
    });
    const params = getQueryParams(document.currentScript?.getAttribute('src') || '');
    Sentry.setTag('paymentToken', params?.paymentToken || 'unknown');
    Sentry.setTag('correlationId', params?.correlationId || 'unknown');
}

function defineWebComponents() {
    if (customElements.get('netvalve-tokenfields')) return; // prevent duplicate definitions

    customElements.define('netvalve-tokenfields', NetvalveTokenFieldsContainer);
    customElements.define('netvalve-token-field-base', NetvalveTokenizerBase);
    customElements.define('netvalve-cardnumber', NetvalveCardNumber);
    customElements.define('netvalve-cvv', NetvalveCVV);
    customElements.define('netvalve-expiry', NetvalveExpiry);
    console.log('--- DEFINED WEB COMPONENTS ---');
}

// Loading web components
if (document.readyState === 'complete' || document.readyState === 'interactive') defineWebComponents();
else document.addEventListener('DOMContentLoaded', defineWebComponents);
window.addEventListener('load', defineWebComponents);

/**
 * Configures and initializes a token field element (card number, CVV, or expiry).
 *
 * @template T - Type of field element (NetvalveCVV | NetvalveExpiry | NetvalveCardNumber)
 * @param {FieldName} fieldType - Type of field to configure ('cardNumber', 'cardCvv', or 'cardExpiry')
 * @param {TokenFieldsConfiguration} config - Configuration object containing field settings
 * @returns {T | null} Configured field element or null if configuration fails
 * @throws {Error} If neither an existing field element is found nor a container selector is provided
 *
 * The function:
 * 1. Checks for an existing field element in the DOM
 * 2. If not found, creates a new element using the provided container selector
 * 3. Applies configuration attributes to the field element
 * 4. Appends the element to its container if newly created
 */

function configureField<T extends NetvalveCVV | NetvalveExpiry | NetvalveCardNumber>(
    fieldType: FieldName,
    config: TokenFieldsConfiguration
): T | null {
    let fieldEl: T | null = null;
    let fieldContainerEl: HTMLElement | null = null;

    // Check if the field element already exists in the DOM
    fieldEl = document.querySelector(
        fieldType === 'cardNumber'
            ? 'netvalve-cardnumber'
            : fieldType === 'cardCvv'
              ? 'netvalve-cvv'
              : 'netvalve-expiry'
    ) as T;

    // If there is no existing field element, create a new one and append it to the container
    if (!fieldEl && config.fields?.[fieldType]?.containerSelector) {
        fieldContainerEl = document.querySelector(config.fields?.[fieldType].containerSelector);
        if (!fieldContainerEl) throw new Error(`${fieldType} container element not found for field ${fieldType}`);
        if (fieldType === 'cardNumber') fieldEl = document.createElement('netvalve-cardnumber') as T;
        else if (fieldType === 'cardCvv') fieldEl = document.createElement('netvalve-cvv') as T;
        else if (fieldType === 'cardExpiry') fieldEl = document.createElement('netvalve-expiry') as T;
    } else if (!fieldEl) {
        throw new Error(
            `There is neither an existing ${fieldType} element nor a container selector provided in the configuration.`
        );
    }
    if (config.fields?.[fieldType]) applyCommonAttributes(fieldEl, config.fields[fieldType]); // add the attributes to the field element
    if (fieldContainerEl) fieldContainerEl.appendChild(fieldEl); // if a new element was created, append it to the container

    return fieldEl || null;
}

/**
 * Immediately Invoked Function Expression (IIFE) that initializes the Netvalve token fields SDK.
 * Sets up the global Netvalve object and exposes the SDK's public interface.
 *
 * Global interface exposed:
 * window.Netvalve = {
 *   initTokenFields: (config) => Promise<SDKMethods>,
 *   tokenizeFields?: () => Promise<string | null>,
 *   allFieldsTokenized?: () => boolean,
 *   validateFields?: () => Promise<boolean>,
 *   isTokenizing?: () => boolean
 * }
 *
 * @fires CustomEvent#netvalve-sdk-ready - Fired when SDK is initialized and ready
 */
(() => {
    let cvv: NetvalveCVV | null = null;
    let expiry: NetvalveExpiry | null = null;
    let cardNumber: NetvalveCardNumber | null = null;

    async function validateFields(): Promise<boolean> {
        const result =
            ((await cardNumber?.validate())?.success ?? false) &&
            ((await cvv?.validate())?.success ?? false) &&
            ((await expiry?.validate())?.success ?? false);
        return result;
    }

    /**
     * Initializes the Netvalve token fields SDK by setting up the payment form and field elements.
     * Called by the merchant when the page is loaded.
     * @param {TokenFieldsConfiguration} config - Configuration object containing form selectors, styling, and callback options
     * @returns {Promise<{
     *   validateFields: () => Promise<boolean>,
     *   tokenizeFields: () => Promise<string | null>,
     *   allFieldsTokenized: () => boolean,
     *   isTokenizing: () => boolean
     * }>} Object containing SDK methods for field validation, tokenization status, and form submission
     * @throws {Error} If form element cannot be found or initialization fails
     *
     * The function:
     * 1. Creates/configures payment field elements (card number, CVV, expiry)
     * 2. Sets up form interception for payment submission
     * 3. Applies custom styling and validation rules
     * 4. Attaches SDK methods to the global Netvalve object
     */
    function initTokenFields(config: TokenFieldsConfiguration) {
        let form;
        try {
            if (!config.formSelector && !config.formId)
                throw new Error('A selector for the form element, or the form container element, is required');

            return Promise.all([
                customElements.whenDefined('netvalve-tokenfields'),
                customElements.whenDefined('netvalve-cardnumber'),
                customElements.whenDefined('netvalve-cvv'),
                customElements.whenDefined('netvalve-expiry'),
            ]).then(() => {
                form = getFormElement(config.formId ? `#${config.formId}` : config?.formSelector); // allow for a formId or a selector
                if (!form) throw new Error('Form element not found');
                console.log('---FOUND FORM FROM JS INIT ---', form);

                // configure the fields
                cardNumber = configureField<NetvalveCardNumber>('cardNumber', config);
                cvv = configureField<NetvalveCVV>('cardCvv', config);
                expiry = configureField<NetvalveExpiry>('cardExpiry', config);

                // check if the netvalve-tokenfields element is already in the dom
                const existingNetvalveTokenizeElement = document.querySelector(
                    'netvalve-tokenfields'
                ) as NetvalveTokenFieldsContainer;

                // configure the netvalve-tokenfields element
                const netvalveTokenizeElement =
                    existingNetvalveTokenizeElement ||
                    (document.createElement('netvalve-tokenfields') as NetvalveTokenFieldsContainer);
                netvalveTokenizeElement.form = form;
                netvalveTokenizeElement.formSelector = config.formSelector;

                if (config.interceptFormSubmit !== false) {
                    if (config.payButtonId) netvalveTokenizeElement.submitButtonId = config.payButtonId;

                    if (config.onSubmitPayment) {
                        netvalveTokenizeElement.interceptFormSubmit(
                            wrapCallback(config.onSubmitPayment, 'onSubmitPayment'),
                            config.payButtonId
                        );
                    }
                } else netvalveTokenizeElement.interceptFormSubmission = false;

                if (config.defaultIframeHeight)
                    netvalveTokenizeElement.defaultIframeHeight = config.defaultIframeHeight;

                if (config.inputStyles) Object.assign(netvalveTokenizeElement.style, config.inputStyles);

                if (config.invalidStyles) netvalveTokenizeElement.invalidStyles = config.invalidStyles;

                if (config.placeholderStyles) netvalveTokenizeElement.placeholderStyles = config.placeholderStyles;

                if (config.font) netvalveTokenizeElement.font = config.font;

                netvalveTokenizeElement.showCardLogo = config.showCardLogo ?? true;

                if (config.hidePlaceholder) netvalveTokenizeElement.hidePlaceholder = config.hidePlaceholder;

                if (config.onValidate)
                    netvalveTokenizeElement.onValidate = wrapCallback(config.onValidate, 'onValidate');
                // If it's a new element, append it to DOM
                if (!existingNetvalveTokenizeElement) {
                    console.log('--- APPENDING NEW NETVALVE TOKENIZE ELEMENT ---');
                    // wrap the form in the netvalve-tokenfields element. This will trigger style collection and ready state
                    form.parentNode!.insertBefore(netvalveTokenizeElement, form);
                    netvalveTokenizeElement.appendChild(form);
                } else {
                    // If element exists, manually trigger style collection and ready state
                    console.log('--- DISPATCHING INHERITABLE STYLES FROM JS CONFIG ---');
                    netvalveTokenizeElement.dispatchInheritableStyles();
                }

                // SDK Methods
                const tokenizeFields = netvalveTokenizeElement.tokenizeFields.bind(netvalveTokenizeElement);
                const allFieldsTokenized = netvalveTokenizeElement.allFieldsTokenized.bind(netvalveTokenizeElement);
                const isTokenizing = netvalveTokenizeElement.isTokenizing.bind(netvalveTokenizeElement);

                // Attach to window object
                window.Netvalve.tokenizeFields = tokenizeFields;
                window.Netvalve.allFieldsTokenized = allFieldsTokenized;
                window.Netvalve.validateFields = validateFields;
                window.Netvalve.isTokenizing = isTokenizing;

                return {
                    validateFields,
                    tokenizeFields,
                    allFieldsTokenized,
                    isTokenizing,
                };
            });
        } catch (error) {
            sentryLog({
                name: 'JS_INITIATE',
                msg: typeof error === 'string' ? error : 'Error initializing token fields with JS SDK',
                error: error as Error,
            });
            throw error; // prevent the sdk from initializing
        }
    }

    window.Netvalve = { initTokenFields };
    window.dispatchEvent(new CustomEvent('netvalve-sdk-ready', { detail: { success: true } }));
})();
